import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useIsFocused } from '@react-navigation/native';
import { useIsTabActive } from '../navigation/tabActivity';
import PromotionsTicker, { type PromotionsTickerHandle } from './PromotionsTicker';
import Icon from 'react-native-vector-icons/Feather';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { useI18n } from '../i18n';
import { formatPromotionDateRange, type FeedPromotion, type PublishedPromotion } from '../utils/promotions';

// Seasonal / editorial collections on the Events feed. Deliberately compact
// (fixed card height, no entrance animation) so it never pushes the actual
// events far down. Several collections roll slowly as a ticker.

const MOBILE_CARD_HEIGHT = 104;
const DESKTOP_CARD_HEIGHT = 108;
const GAP = 10;
// How much of the next card peeks in on mobile when there are several.
const PEEK = 44;
const DESKTOP_MAX_COLUMNS = 3;
const SINGLE_DESKTOP_MAX_WIDTH = 760;
// Continuous ticker speed: one card-width every six seconds, with no dwell
// between cards. It only pauses while the user is holding or dragging it.
const AUTO_ROLL_MS = 6000;

const PRESS_RETENTION = { top: 200, bottom: 200, left: 400, right: 400 };
const OUTFIT = Platform.select({ web: { fontFamily: 'Outfit, sans-serif' }, default: {} });
// Keeps the copy readable over bright parts of any cover photo.
const TEXT_SHADOW = { textShadowColor: 'rgba(0,0,0,0.45)', textShadowOffset: { width: 0, height: 1 }, textShadowRadius: 6 };

type Props = {
  items: FeedPromotion[];
  desktop: boolean;
  onPress: (promotion: PublishedPromotion) => void;
  // Admins/co-admins only: shows a "Manage" link (and keeps the header
  // visible when nothing is live, so promotions can always be reached).
  onManage?: () => void;
  testID?: string;
};

const PromotionsSection: React.FC<Props> = ({ items, desktop, onPress, onManage, testID }) => {
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemedStyles(theme), [theme]);
  const [width, setWidth] = useState(0);
  const screenFocused = useIsFocused();
  const tabActive = useIsTabActive();
  const focused = screenFocused && tabActive;
  const tickerRef = useRef<PromotionsTickerHandle>(null);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const next = Math.round(event.nativeEvent.layout.width);
    setWidth(current => (current === next ? current : next));
  }, []);

  const single = items.length <= 1;
  const height = desktop ? DESKTOP_CARD_HEIGHT : MOBILE_CARD_HEIGHT;
  const desktopGrid = desktop && items.length <= DESKTOP_MAX_COLUMNS;
  let cardWidth: number;
  if (!width) cardWidth = 0;
  else if (!items.length) cardWidth = 0;
  else if (desktop) {
    cardWidth = single
      ? Math.min(width, SINGLE_DESKTOP_MAX_WIDTH)
      : desktopGrid
        ? Math.floor((width - GAP * (items.length - 1)) / items.length)
        : Math.floor((width - GAP * (DESKTOP_MAX_COLUMNS - 1)) / DESKTOP_MAX_COLUMNS - PEEK / DESKTOP_MAX_COLUMNS);
  } else {
    cardWidth = single ? width : width - PEEK;
  }

  const step = cardWidth + GAP;
  const carouselItems = single ? items : [items[items.length - 1], ...items, items[0]];

  const canRoll = !single && cardWidth > 0 && focused;
  const pause = useCallback(() => tickerRef.current?.pause(), []);
  const resume = useCallback(() => tickerRef.current?.resume(), []);

  const cards = cardWidth > 0
    ? carouselItems.map((item, index) => (
      <PromotionCard
        key={`${item.promotion.id}-${index}`}
        promotion={item.promotion}
        eventCount={item.events.length}
        width={cardWidth}
        height={height}
        language={language}
        onPress={onPress}
        onPressIn={single ? undefined : pause}
        onPressOut={single ? undefined : resume}
      />
    ))
    : null;

  if (!items.length && !onManage) return null;

  return (
    <View style={styles.section} onLayout={onLayout} testID={testID}>
      <View style={styles.header}>
        <View style={[styles.headerIcon, { backgroundColor: theme.colors.chipBackgroundActive, borderColor: `${theme.colors.accent}3D` }]}>
          <Icon name="star" size={12} color={theme.colors.accent} />
        </View>
        <Text style={[styles.headerText, themed.headerText, OUTFIT]}>{t('promotions.sectionTitle')}</Text>
        {onManage ? (
          <Pressable onPress={onManage} style={styles.manage} accessibilityRole="button" hitSlop={8}>
            <Icon name="edit-2" size={11} color={theme.colors.accentSecondary} />
            <Text style={[styles.manageText, { color: theme.colors.accentSecondary }, OUTFIT]}>{t('promotionsAdmin.manage')}</Text>
          </Pressable>
        ) : null}
      </View>
      {!items.length ? (
        <Text style={[styles.adminEmpty, themed.headerText, OUTFIT]}>{t('promotionsAdmin.feedEmpty')}</Text>
      ) : null}
      {/* Reserve the card height before measurement so nothing below shifts. */}
      {items.length ? <View style={{ minHeight: height }}>
        {single ? (
          <View style={styles.row}>{cards}</View>
        ) : (
          <PromotionsTicker
            ref={tickerRef}
            step={step}
            count={items.length}
            rollMs={AUTO_ROLL_MS}
            enabled={canRoll}
            contentContainerStyle={styles.row}
            // Bleed to the screen edge on mobile so the peeking card isn't clipped by the list padding.
            style={desktop ? undefined : styles.bleed}
          >
            {cards}
          </PromotionsTicker>
        )}
      </View> : null}
    </View>
  );
};

type CardProps = {
  promotion: PublishedPromotion;
  eventCount: number;
  width: number;
  height: number;
  language: string;
  onPress: (promotion: PublishedPromotion) => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
};

const PromotionCard: React.FC<CardProps> = memo(({ promotion, eventCount, width, height, language, onPress, onPressIn, onPressOut }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const [imageFailed, setImageFailed] = useState(false);
  const dateRange = useMemo(() => formatPromotionDateRange(promotion, language), [promotion, language]);
  const cta = promotion.ctaLabel || t('promotions.explore');
  const countLabel = t('promotions.eventsCount').replace('{count}', String(eventCount));
  const showImage = Boolean(promotion.coverImageUrl) && !imageFailed;

  return (
    <Pressable
      onPress={() => onPress(promotion)}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      // The card can still be sliding when React Native measures it, so a real
      // finger's tiny movements read as "left the card" and the tap was dropped
      // (only on devices — simulator clicks never move). A swipe still cancels
      // the press natively (the scroll view takes over the touch).
      pressRetentionOffset={PRESS_RETENTION}
      accessibilityRole="button"
      accessibilityLabel={[promotion.title, promotion.subtitle, dateRange, countLabel, cta].filter(Boolean).join('. ')}
      style={({ pressed }) => [
        styles.card,
        { width, height, borderColor: theme.colors.cardBorder },
        theme.shadows.card,
        pressed && styles.cardPressed,
      ]}
    >
      <LinearGradient
        colors={theme.isDark ? ['#123C49', '#59344E', '#F27D26'] : ['#168D8B', '#A76D43', '#F27D26']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      {!showImage ? (
        <View style={styles.fallbackIcon} pointerEvents="none">
          <Icon name="star" size={88} color="rgba(255,255,255,0.14)" />
        </View>
      ) : (
        <Image
          source={{ uri: promotion.coverImageUrl! }}
          style={StyleSheet.absoluteFill}
          resizeMode="cover"
          resizeMethod="resize"
          onError={() => setImageFailed(true)}
          accessibilityIgnoresInvertColors
        />
      )}
      <LinearGradient
        colors={['rgba(4,7,14,0.48)', 'rgba(4,7,14,0.28)', 'rgba(4,7,14,0.82)']}
        locations={[0, 0.45, 1]}
        style={StyleSheet.absoluteFill}
      />
      <LinearGradient
        colors={['#26D0CE', '#F27D26']}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={styles.accentRail}
      />
      <View style={styles.topHighlight} />
      <View style={styles.cardContent}>
        <View style={styles.cardCopy}>
          <View style={styles.dateRow}>
            <Icon name="calendar" size={10} color="rgba(255,255,255,0.82)" />
            <Text style={[styles.dateText, OUTFIT]} numberOfLines={1}>{dateRange}</Text>
          </View>
          <Text style={[styles.title, OUTFIT]} numberOfLines={1}>{promotion.title}</Text>
          {promotion.subtitle ? (
            <Text style={[styles.subtitle, OUTFIT]} numberOfLines={1}>{promotion.subtitle}</Text>
          ) : null}
        </View>
        <View style={styles.cardAction}>
          <View style={styles.countBadge}>
            <Text style={[styles.count, OUTFIT]} numberOfLines={1}>{countLabel}</Text>
          </View>
          <View style={styles.chevronButton}>
            <Icon name="chevron-right" size={16} color="#fff" />
          </View>
        </View>
      </View>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  section: { marginTop: 7, marginBottom: 16 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 10 },
  headerIcon: {
    width: 26,
    height: 26,
    borderRadius: 9,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: { fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 1.6, textTransform: 'uppercase' },
  manage: { marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: 4 },
  manageText: { fontSize: 12, fontWeight: Typography.extrabold },
  adminEmpty: { fontSize: 12, marginBottom: 4 },
  row: { flexDirection: 'row', gap: GAP },
  bleed: { marginRight: -16 },
  card: { borderRadius: 0, borderWidth: 1, overflow: 'hidden' },
  cardPressed: { opacity: 0.92, transform: [{ scale: 0.985 }] },
  accentRail: { position: 'absolute', left: 0, top: 14, bottom: 14, width: 3, borderRadius: 99 },
  topHighlight: {
    position: 'absolute',
    left: 18,
    right: 18,
    top: 0,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.42)',
  },
  fallbackIcon: { position: 'absolute', right: -4, bottom: -34, transform: [{ rotate: '-12deg' }] },
  cardContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  cardCopy: { flex: 1, minWidth: 0, gap: 1 },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 3,
    alignSelf: 'flex-start',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 99,
    backgroundColor: 'rgba(7,12,20,0.42)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  dateText: { color: 'rgba(255,255,255,0.92)', fontSize: 9, fontWeight: Typography.extrabold, letterSpacing: 0.2 },
  title: { color: '#fff', fontSize: 18, lineHeight: 22, fontWeight: Typography.extrabold, letterSpacing: -0.15, ...TEXT_SHADOW },
  subtitle: { color: 'rgba(255,255,255,0.86)', fontSize: 11, lineHeight: 15, fontWeight: Typography.semibold, ...TEXT_SHADOW },
  cardAction: { flexDirection: 'row', alignItems: 'center', gap: 7, flexShrink: 0 },
  countBadge: {
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 99,
    backgroundColor: 'rgba(7,12,20,0.46)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  count: { color: '#fff', fontSize: 10, fontWeight: Typography.extrabold },
  chevronButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(242,125,38,0.92)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
  },
});

const createThemedStyles = (theme: TonyteTheme) => StyleSheet.create({
  headerText: { color: theme.colors.textMuted },
});

export default PromotionsSection;
